import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import type { JSX } from "react";

import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { LoginScreen } from "@/features/auth/login-screen";
import { CaseWorkspacePage, CasesListPage } from "@/features/cases/pages";
import { markdownToPlanDocument, planToMarkdown } from "@/features/cases/plan-markdown";
import {
  DocumentLibraryPage,
  PlaybookPage,
  PromptTemplatePage,
  SettingsPage,
  TendersPage
} from "@/features/app/content-pages";
import { ThemeIcon, getNavLabel, navGroups } from "@/features/app/navigation";
import type {
  ActivityEventRow,
  BrowserEntry,
  CasePage,
  CaseReadiness,
  CaseRow,
  CompanyScope,
  FeasibilityRow,
  ImportFileRow,
  JobLogRow,
  JobRow,
  PlanRow,
  ProposalRow,
  QuestionAnswerDraft,
  QuestionRow,
  ThemeMode
} from "@/features/app/types";
import {
  applyThemeToDom,
  buildQuestionAnswerDraft,
  isActiveJobStatus,
  isDarkBySystemPreference,
  isMarkdownPath,
  toQuestionAnswerPayload
} from "@/features/app/utils";
import { cn } from "@/lib/utils";
import { buildAppPath, parseAppPath, routeForNavSelection, type NavSlug } from "./lib/app-routing";
import { authClient } from "./lib/auth-client";

const CLAUDE_MODELS = [
  "claude-sonnet-4-6",
  "claude-opus-4-6",
  "claude-haiku-4-5"
] as const;

type PromptTemplateId = "question-prompt" | "plan-prompt" | "feasibility-prompt";

type CaseEventsPayload =
  | {
      type: "job_updated";
      job: JobRow;
    }
  | {
      type: "job_log";
      log: JobLogRow;
    }
  | {
      type: "activity_event";
      event: ActivityEventRow;
    }
  | {
      type: "connected";
      caseId: string;
    }
  | {
      type: "heartbeat";
      timestamp: string;
    };

type LibraryImportActivity = {
  status: "idle" | "running" | "succeeded" | "failed";
  title: string;
  detail: string;
  progress: number;
  error: string | null;
};

const idleLibraryImportActivity = (): LibraryImportActivity => ({
  status: "idle",
  title: "",
  detail: "",
  progress: 0,
  error: null
});

type LibraryImportJobPayload = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  progress: number;
  detail: string;
  error: string | null;
  importedPaths: string[];
  totalFiles: number;
  completedFiles: number;
};

const mergeJob = (jobs: JobRow[], nextJob: JobRow): JobRow[] => {
  const existingIndex = jobs.findIndex((job) => job.id === nextJob.id);
  if (existingIndex < 0) {
    return [nextJob, ...jobs];
  }
  const updated = [...jobs];
  updated[existingIndex] = nextJob;
  return updated;
};

const mergeLog = (logs: JobLogRow[], nextLog: JobLogRow): JobLogRow[] => {
  if (logs.some((log) => log.id === nextLog.id)) {
    return logs;
  }
  return [nextLog, ...logs].sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime());
};

const mergeActivityEvent = (events: ActivityEventRow[], nextEvent: ActivityEventRow): ActivityEventRow[] => {
  if (events.some((event) => event.id === nextEvent.id)) {
    return events;
  }
  return [...events, nextEvent].sort((left, right) => {
    const leftTime = new Date(left.created_at).getTime();
    const rightTime = new Date(right.created_at).getTime();
    return leftTime - rightTime || left.id.localeCompare(right.id);
  });
};

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const markCaseOutlineStale = (
  cases: CaseRow[],
  caseId: string,
  subState: CaseRow["sub_state"]
): CaseRow[] =>
  cases.map((caseRow) =>
    caseRow.id === caseId
      ? { ...caseRow, state: "questions_generated", sub_state: subState }
      : caseRow
  );

const AuthenticatedApp = (): JSX.Element => {
  const [activeItem, setActiveItem] = useState<NavSlug>(() => {
    if (typeof window === "undefined") return "cases";
    return parseAppPath(window.location.pathname, window.location.search).activeItem;
  });
  const [theme, setTheme] = useState<ThemeMode>("system");
  const [statusMessage, setStatusMessage] = useState("");
  const [isDeleteCasesDialogOpen, setIsDeleteCasesDialogOpen] = useState(false);
  const [isDeletingCases, setIsDeletingCases] = useState(false);
  const importTenderInputRef = useRef<HTMLInputElement | null>(null);

  const [cases, setCases] = useState<CaseRow[]>([]);
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return parseAppPath(window.location.pathname, window.location.search).selectedCaseId;
  });
  const [selectedCasePage, setSelectedCasePage] = useState<CasePage>(() => {
    if (typeof window === "undefined") return "overview";
    return parseAppPath(window.location.pathname, window.location.search).casePage;
  });
  const [selectedImportFilePath, setSelectedImportFilePath] = useState(() => {
    if (typeof window === "undefined") return "";
    return parseAppPath(window.location.pathname, window.location.search).selectedImportFilePath ?? "";
  });
  const [importViewerMarkdownContent, setImportViewerMarkdownContent] = useState("");
  const [importViewerError, setImportViewerError] = useState("");
  const [isImportViewerLoading, setIsImportViewerLoading] = useState(false);
  const [caseJobs, setCaseJobs] = useState<JobRow[]>([]);
  const [caseLogs, setCaseLogs] = useState<JobLogRow[]>([]);
  const [activityEvents, setActivityEvents] = useState<ActivityEventRow[]>([]);
  const [caseQuestions, setCaseQuestions] = useState<QuestionRow[]>([]);
  const [casePlan, setCasePlan] = useState<PlanRow | null>(null);
  const [caseProposals, setCaseProposals] = useState<ProposalRow[]>([]);
  const [caseImportFiles, setCaseImportFiles] = useState<ImportFileRow[]>([]);
  const [caseFeasibility, setCaseFeasibility] = useState<FeasibilityRow | null>(null);
  const [caseReadiness, setCaseReadiness] = useState<CaseReadiness>({
    canGenerateQuestions: false,
    canGeneratePlan: false,
    canGenerateProposal: false
  });
  const [selectedImportLogJobId, setSelectedImportLogJobId] = useState<string | null>(null);
  const [selectedImportLogFiles, setSelectedImportLogFiles] = useState<ImportFileRow[]>([]);
  const [notesDraft, setNotesDraft] = useState("");
  const [questionAnswerDrafts, setQuestionAnswerDrafts] = useState<Record<string, QuestionAnswerDraft>>({});
  const [planMarkdown, setPlanMarkdown] = useState("");
  const [proposalNotes, setProposalNotes] = useState("");
  const [proposalFeedback, setProposalFeedback] = useState("");
  const [proposalChatDraft, setProposalChatDraft] = useState("");

  const [playbookContent, setPlaybookContent] = useState("");
  const [questionPromptContent, setQuestionPromptContent] = useState("");
  const [planPromptContent, setPlanPromptContent] = useState("");
  const [feasibilityPromptContent, setFeasibilityPromptContent] = useState("");
  const [settingsModel, setSettingsModel] = useState<string>("claude-sonnet-4-6");

  const [profileEntries, setProfileEntries] = useState<BrowserEntry[]>([]);
  const [cvEntries, setCvEntries] = useState<BrowserEntry[]>([]);
  const [selectedProfilePath, setSelectedProfilePath] = useState("");
  const [selectedCvPath, setSelectedCvPath] = useState("");
  const [profileEditorValue, setProfileEditorValue] = useState("");
  const [cvEditorValue, setCvEditorValue] = useState("");
  const [newProfileDirectory, setNewProfileDirectory] = useState("");
  const [newProfileFileName, setNewProfileFileName] = useState("new-file.md");
  const [newCvDirectory, setNewCvDirectory] = useState("");
  const [newCvFileName, setNewCvFileName] = useState("new-cv.md");
  const [uploadProfileDirectory, setUploadProfileDirectory] = useState("");
  const [uploadCvDirectory, setUploadCvDirectory] = useState("");
  const [profileImportActivity, setProfileImportActivity] = useState<LibraryImportActivity>(idleLibraryImportActivity);
  const [cvImportActivity, setCvImportActivity] = useState<LibraryImportActivity>(idleLibraryImportActivity);
  const hasSyncedUrlRef = useRef(false);

  const selectedCase = useMemo(() => cases.find((caseRow) => caseRow.id === selectedCaseId) ?? null, [cases, selectedCaseId]);
  const selectedProfileEntry = useMemo(
    () => profileEntries.find((entry) => entry.path === selectedProfilePath) ?? null,
    [profileEntries, selectedProfilePath]
  );
  const selectedCvEntry = useMemo(
    () => cvEntries.find((entry) => entry.path === selectedCvPath) ?? null,
    [cvEntries, selectedCvPath]
  );
  const hasRunningCases = useMemo(
    () => cases.some((caseRow) => caseRow.running_job && isActiveJobStatus(caseRow.running_job.status)),
    [cases]
  );
  const hasRunningSelectedCaseJobs = useMemo(() => {
    const selectedCaseIsRunning =
      selectedCase?.running_job !== null && selectedCase?.running_job !== undefined
        ? isActiveJobStatus(selectedCase.running_job.status)
        : false;
    const detailJobIsRunning = caseJobs.some((job) => isActiveJobStatus(job.status));
    return selectedCaseIsRunning || detailJobIsRunning;
  }, [caseJobs, selectedCase]);

  const refreshCases = async (): Promise<void> => {
    const response = await fetch("/api/cases");
    if (!response.ok) return;
    const payload = (await response.json()) as { cases: CaseRow[] };
    setCases(payload.cases);
  };

  const refreshCaseDetail = async (caseId: string): Promise<void> => {
    const response = await fetch(`/api/cases/${caseId}`);
    if (!response.ok) return;
    const payload = (await response.json()) as {
      case: CaseRow;
      jobs: JobRow[];
      logs: JobLogRow[];
      activityEvents: ActivityEventRow[];
      questions: QuestionRow[];
      plan: PlanRow | null;
      proposals: ProposalRow[];
      importFiles: ImportFileRow[];
      feasibility: FeasibilityRow | null;
      readiness: CaseReadiness;
    };

    setCaseJobs(payload.jobs);
    setCaseLogs(payload.logs);
    setActivityEvents(payload.activityEvents);
    setCaseQuestions(payload.questions);
    setCasePlan(payload.plan);
    setCaseProposals(payload.proposals);
    setCaseImportFiles(payload.importFiles);
    setCaseFeasibility(payload.feasibility);
    setCaseReadiness(payload.readiness);
    setNotesDraft(payload.case.notes ?? "");
    setQuestionAnswerDrafts(
      Object.fromEntries(
        payload.questions.map((question) => [
          question.id,
          buildQuestionAnswerDraft(question)
        ])
      )
    );
    setPlanMarkdown(planToMarkdown(payload.plan));
  };

  const refreshBrowser = async (scope: CompanyScope): Promise<void> => {
    const response = await fetch(`/api/company/browser?scope=${scope}`);
    if (!response.ok) return;
    const payload = (await response.json()) as { entries: BrowserEntry[] };
    if (scope === "profile") {
      setProfileEntries(payload.entries);
      return;
    }
    setCvEntries(payload.entries);
  };

  const loadPromptTemplate = async (promptId: PromptTemplateId): Promise<void> => {
    const response = await fetch(`/api/prompt-templates/${promptId}`);
    if (!response.ok) return;
    const payload = (await response.json()) as { content: string };
    if (promptId === "question-prompt") {
      setQuestionPromptContent(payload.content);
      return;
    }
    if (promptId === "plan-prompt") {
      setPlanPromptContent(payload.content);
      return;
    }
    setFeasibilityPromptContent(payload.content);
  };

  const savePromptTemplate = async (promptId: PromptTemplateId, content: string): Promise<void> => {
    await fetch(`/api/prompt-templates/${promptId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content })
    });
    setStatusMessage("Prompt saved");
  };

  const onToggleImportLogDetails = async (jobId: string): Promise<void> => {
    if (!selectedCaseId) return;
    if (selectedImportLogJobId === jobId) {
      setSelectedImportLogJobId(null);
      setSelectedImportLogFiles([]);
      return;
    }
    const response = await fetch(`/api/cases/${selectedCaseId}/import-files?jobId=${encodeURIComponent(jobId)}`);
    if (!response.ok) return;
    const payload = (await response.json()) as { importFiles: ImportFileRow[] };
    setSelectedImportLogJobId(jobId);
    setSelectedImportLogFiles(payload.importFiles);
  };

  useEffect(() => {
    const loadTheme = async (): Promise<void> => {
      try {
        const response = await fetch("/api/settings/theme");
        if (!response.ok) return;
        const payload = (await response.json()) as { theme?: ThemeMode };
        const resolvedTheme = payload.theme ?? "system";
        setTheme(resolvedTheme);
        applyThemeToDom(resolvedTheme);
      } catch {}
    };
    void loadTheme();
    void refreshCases();
  }, []);

  useEffect(() => {
    if (theme !== "system") return;
    const mediaQuery = globalThis.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mediaQuery) return;
    const listener = (): void => applyThemeToDom("system");
    mediaQuery.addEventListener("change", listener);
    return () => mediaQuery.removeEventListener("change", listener);
  }, [theme]);

  useEffect(() => {
    if (!selectedCaseId) {
      setSelectedImportLogJobId(null);
      setSelectedImportLogFiles([]);
      setSelectedCasePage("overview");
      setSelectedImportFilePath("");
      setImportViewerMarkdownContent("");
      setImportViewerError("");
      setIsImportViewerLoading(false);
      setCaseJobs([]);
      setCaseLogs([]);
      setActivityEvents([]);
      setCaseQuestions([]);
      setCasePlan(null);
      setCaseProposals([]);
      setCaseImportFiles([]);
      setCaseFeasibility(null);
      setCaseReadiness({ canGenerateQuestions: false, canGeneratePlan: false, canGenerateProposal: false });
      setPlanMarkdown("");
      return;
    }
    setSelectedImportLogJobId(null);
    setSelectedImportLogFiles([]);
    void refreshCaseDetail(selectedCaseId);
  }, [selectedCaseId]);

  useEffect(() => {
    if (activeItem !== "cases" || !selectedCaseId || !hasRunningSelectedCaseJobs) {
      return;
    }
    const eventSource = new EventSource(`/api/cases/${selectedCaseId}/events`);
    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data) as CaseEventsPayload;
        if (payload.type === "job_updated") {
          setCaseJobs((current) => mergeJob(current, payload.job));
          setCases((current) =>
            current.map((caseRow) => {
              if (caseRow.id !== selectedCaseId) {
                return caseRow;
              }
              const isActive = isActiveJobStatus(payload.job.status);
              return {
                ...caseRow,
                running_job: isActive
                  ? {
                      id: payload.job.id,
                      kind: payload.job.kind,
                      status: payload.job.status,
                      progress: payload.job.progress,
                      created_at: payload.job.created_at,
                      updated_at: payload.job.updated_at
                    }
                  : null
              };
            })
          );
          if (!isActiveJobStatus(payload.job.status)) {
            void refreshCases();
            void refreshCaseDetail(selectedCaseId);
          }
          return;
        }
        if (payload.type === "job_log") {
          setCaseLogs((current) => mergeLog(current, payload.log));
          return;
        }
        if (payload.type === "activity_event") {
          setActivityEvents((current) => mergeActivityEvent(current, payload.event));
        }
      } catch {}
    };
    eventSource.onerror = () => {
      eventSource.close();
      void refreshCases();
      void refreshCaseDetail(selectedCaseId);
    };
    return () => eventSource.close();
  }, [activeItem, hasRunningSelectedCaseJobs, selectedCaseId]);

  useEffect(() => {
    if (!selectedCaseId || !selectedImportFilePath) {
      setImportViewerMarkdownContent("");
      setImportViewerError("");
      setIsImportViewerLoading(false);
      return;
    }
    if (!isMarkdownPath(selectedImportFilePath)) {
      setImportViewerMarkdownContent("");
      setImportViewerError("");
      setIsImportViewerLoading(false);
      return;
    }
    const controller = new AbortController();
    void (async () => {
      try {
        setIsImportViewerLoading(true);
        setImportViewerError("");
        const response = await fetch(
          `/api/cases/${selectedCaseId}/import-file?path=${encodeURIComponent(selectedImportFilePath)}`,
          { signal: controller.signal }
        );
        if (!response.ok) {
          setImportViewerError("Could not load markdown file.");
          setImportViewerMarkdownContent("");
          setIsImportViewerLoading(false);
          return;
        }
        const content = await response.text();
        setImportViewerMarkdownContent(content);
        setIsImportViewerLoading(false);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setImportViewerError("Could not load markdown file.");
        setImportViewerMarkdownContent("");
        setIsImportViewerLoading(false);
      }
    })();
    return () => controller.abort();
  }, [selectedCaseId, selectedImportFilePath]);

  useEffect(() => {
    if (activeItem !== "cases") return;
    const shouldPoll = selectedCaseId ? hasRunningSelectedCaseJobs : hasRunningCases;
    if (!shouldPoll) return;
    const intervalId = window.setInterval(() => {
      if (selectedCaseId) void refreshCaseDetail(selectedCaseId);
      void refreshCases();
    }, 10_000);
    return () => window.clearInterval(intervalId);
  }, [activeItem, hasRunningCases, hasRunningSelectedCaseJobs, selectedCaseId]);

  useEffect(() => {
    const onPopState = (): void => {
      const route = parseAppPath(window.location.pathname, window.location.search);
      setActiveItem(route.activeItem);
      setSelectedCaseId(route.selectedCaseId);
      setSelectedCasePage(route.casePage);
      setSelectedImportFilePath(route.selectedImportFilePath ?? "");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const existingCaseIds = new Set(cases.map((caseRow) => caseRow.id));
    setSelectedCaseIds((current) => current.filter((caseId) => existingCaseIds.has(caseId)));
  }, [cases]);

  useEffect(() => {
    const nextPath = buildAppPath({
      activeItem,
      selectedCaseId: activeItem === "cases" ? selectedCaseId : null,
      casePage: activeItem === "cases" ? selectedCasePage : "overview",
      selectedImportFilePath: activeItem === "cases" && (selectedCasePage === "files") ? selectedImportFilePath : null
    });
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl === nextPath) {
      hasSyncedUrlRef.current = true;
      return;
    }
    if (hasSyncedUrlRef.current) {
      window.history.pushState(null, "", nextPath);
      return;
    }
    window.history.replaceState(null, "", nextPath);
    hasSyncedUrlRef.current = true;
  }, [activeItem, selectedCaseId, selectedCasePage, selectedImportFilePath]);
  useEffect(() => {
    if (activeItem === "playbook") {
      void (async () => {
        const response = await fetch("/api/playbook");
        if (!response.ok) return;
        const payload = (await response.json()) as { content: string };
        setPlaybookContent(payload.content);
      })();
    }
    if (activeItem === "question-prompt") void loadPromptTemplate("question-prompt");
    if (activeItem === "plan-prompt") void loadPromptTemplate("plan-prompt");
    if (activeItem === "feasibility-prompt") void loadPromptTemplate("feasibility-prompt");
    if (activeItem === "profile-references") void refreshBrowser("profile");
    if (activeItem === "cvs") void refreshBrowser("cvs");
    if (activeItem === "settings") {
      void (async () => {
        const response = await fetch("/api/settings");
        if (!response.ok) return;
        const payload = (await response.json()) as { llm: { defaultModel: string } };
        setSettingsModel(payload.llm.defaultModel);
      })();
    }
  }, [activeItem]);

  useEffect(() => {
    if (!selectedProfilePath || !selectedProfileEntry?.isMarkdown) {
      setProfileEditorValue("");
      return;
    }
    void (async () => {
      const response = await fetch(`/api/company/file?scope=profile&path=${encodeURIComponent(selectedProfilePath)}`);
      if (!response.ok) return;
      const payload = (await response.json()) as { content: string };
      setProfileEditorValue(payload.content);
    })();
  }, [selectedProfileEntry, selectedProfilePath]);

  useEffect(() => {
    if (!selectedCvPath || !selectedCvEntry?.isMarkdown) {
      setCvEditorValue("");
      return;
    }
    void (async () => {
      const response = await fetch(`/api/company/file?scope=cvs&path=${encodeURIComponent(selectedCvPath)}`);
      if (!response.ok) return;
      const payload = (await response.json()) as { content: string };
      setCvEditorValue(payload.content);
    })();
  }, [selectedCvEntry, selectedCvPath]);

  const toggleTheme = async (): Promise<void> => {
    const nextTheme: ThemeMode = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    applyThemeToDom(nextTheme);
    await fetch("/api/settings/theme", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme: nextTheme })
    });
  };

  const onImportZip = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.set("file", file);
    setStatusMessage("Import started...");
    const response = await fetch("/api/cases/import", { method: "POST", body: data });
    if (!response.ok) {
      setStatusMessage("Import failed to start");
      return;
    }
    const payload = (await response.json()) as { caseId: string };
    setStatusMessage("Import & assessment job queued");
    await refreshCases();
    setActiveItem("cases");
    setSelectedCaseId(payload.caseId);
    setSelectedCasePage("overview");
    setSelectedImportFilePath("");
    event.target.value = "";
  };

  const onToggleCaseSelection = (caseId: string, checked: boolean): void => {
    setSelectedCaseIds((current) => {
      if (checked) return current.includes(caseId) ? current : [...current, caseId];
      return current.filter((id) => id !== caseId);
    });
  };

  const onToggleSelectVisibleCases = (caseIds: string[], checked: boolean): void => {
    if (!checked) {
      setSelectedCaseIds((current) => current.filter((caseId) => !caseIds.includes(caseId)));
      return;
    }
    setSelectedCaseIds((current) => {
      const next = new Set(current);
      for (const caseId of caseIds) next.add(caseId);
      return Array.from(next);
    });
  };

  const onDeleteSelectedCases = async (): Promise<void> => {
    if (selectedCaseIds.length === 0) return;
    const caseIdsToDelete = [...selectedCaseIds];
    setIsDeletingCases(true);
    const response = await fetch("/api/cases/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ caseIds: caseIdsToDelete })
    });
    setIsDeletingCases(false);
    if (!response.ok) {
      setStatusMessage("Failed to delete selected proposals");
      return;
    }
    if (selectedCaseId && caseIdsToDelete.includes(selectedCaseId)) {
      setSelectedCaseId(null);
      setSelectedCasePage("overview");
      setSelectedImportFilePath("");
    }
    setIsDeleteCasesDialogOpen(false);
    setSelectedCaseIds([]);
    setStatusMessage(`Deleted ${caseIdsToDelete.length} proposal${caseIdsToDelete.length === 1 ? "" : "s"}`);
    await refreshCases();
  };

  const onSaveCaseMeta = async (): Promise<void> => {
    if (!selectedCaseId) return;
    await fetch(`/api/cases/${selectedCaseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        notes: notesDraft
      })
    });
    setStatusMessage("Proposal saved");
    await refreshCases();
    await refreshCaseDetail(selectedCaseId);
  };

  const onGenerateQuestions = async (): Promise<void> => {
    if (!selectedCaseId) return;
    await fetch(`/api/cases/${selectedCaseId}/questions/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    setStatusMessage(caseQuestions.length > 0 ? "Steering questions rerun triggered" : "Steering questions generation triggered");
    await refreshCaseDetail(selectedCaseId);
    await refreshCases();
  };

  const onUpdateQuestionAnswer = (questionId: string, key: "selectedOption" | "freeText", value: string): void => {
    setQuestionAnswerDrafts((current) => ({
      ...current,
      [questionId]: {
        selectedOption: current[questionId]?.selectedOption ?? "",
        freeText: current[questionId]?.freeText ?? "",
        [key]: value
      }
    }));
  };

  const onSaveAnswer = async (questionId: string): Promise<void> => {
    if (!selectedCaseId) return;
    const draft = questionAnswerDrafts[questionId];
    if (!draft) return;
    const payload = toQuestionAnswerPayload(draft);
    await fetch(`/api/cases/${selectedCaseId}/answers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        questionId,
        selectedOption: payload.selectedOption,
        freeText: payload.freeText
      })
    });
    setStatusMessage("Answer saved");
    await refreshCaseDetail(selectedCaseId);
    await refreshCases();
  };

  const autoSaveAnswer = async (questionId: string, draft: QuestionAnswerDraft): Promise<void> => {
    if (!selectedCaseId) return;
    const payload = toQuestionAnswerPayload(draft);
    await fetch(`/api/cases/${selectedCaseId}/answers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        questionId,
        selectedOption: payload.selectedOption,
        freeText: payload.freeText
      })
    });
    const newAnswer = { selectedOption: payload.selectedOption, freeText: payload.freeText, updatedAt: new Date().toISOString() };
    setCaseQuestions(prev => {
      const updated = prev.map(q => q.id === questionId ? { ...q, answer: newAnswer } : q);
      const allAnswered = updated.length > 0 && updated.every(q =>
        Boolean(q.answer?.selectedOption?.trim() || q.answer?.freeText?.trim())
      );
      setCaseReadiness((readiness) => ({
        ...readiness,
        canGeneratePlan: allAnswered,
        canGenerateProposal: casePlan ? false : readiness.canGenerateProposal
      }));
      if (casePlan) {
        setCases((current) => markCaseOutlineStale(current, selectedCaseId, allAnswered ? null : "awaiting_answers"));
      }
      return updated;
    });
  };

  const autoSaveNotes = async (notes: string): Promise<void> => {
    if (!selectedCaseId) return;
    await fetch(`/api/cases/${selectedCaseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes })
    });
    if (casePlan) {
      setCases((current) =>
        markCaseOutlineStale(current, selectedCaseId, caseReadiness.canGeneratePlan ? null : "awaiting_answers")
      );
      setCaseReadiness((readiness) => ({ ...readiness, canGenerateProposal: false }));
    }
  };

  const onGeneratePlan = async (): Promise<void> => {
    if (!selectedCaseId) return;
    const response = await fetch(`/api/cases/${selectedCaseId}/plan/generate`, { method: "POST" });
    if (!response.ok) {
      setStatusMessage("Could not generate outline yet");
      return;
    }
    setStatusMessage(casePlan ? "Outline rerun triggered" : "Outline generation triggered");
    await refreshCaseDetail(selectedCaseId);
    await refreshCases();
  };

  const onSavePlan = async (status: "draft" | "approved"): Promise<void> => {
    if (!selectedCaseId || !casePlan) return;
    await fetch(`/api/cases/${selectedCaseId}/plan`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        version: casePlan.version,
        status,
        document: markdownToPlanDocument(planMarkdown)
      })
    });
    setStatusMessage(status === "approved" ? "Outline approved" : "Outline draft saved");
    await refreshCaseDetail(selectedCaseId);
    await refreshCases();
  };

  const onRunProposal = async (): Promise<void> => {
    if (!selectedCaseId) return;
    await fetch(`/api/cases/${selectedCaseId}/proposal`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes: proposalNotes, feedback: proposalFeedback })
    });
    setStatusMessage(caseProposals.length > 0 ? "Proposal rerun triggered" : "Proposal+PDF generation triggered");
    await refreshCaseDetail(selectedCaseId);
    await refreshCases();
  };

  const onSendProposalChatMessage = async (): Promise<void> => {
    if (!selectedCaseId || !proposalChatDraft.trim()) return;
    await fetch(`/api/cases/${selectedCaseId}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: proposalChatDraft.trim() })
    });
    setProposalChatDraft("");
    setStatusMessage("Proposal iteration started");
    await refreshCaseDetail(selectedCaseId);
    await refreshCases();
  };

  const onRegeneratePdf = async (version: number): Promise<void> => {
    if (!selectedCaseId) return;
    await fetch(`/api/cases/${selectedCaseId}/proposals/${version}/pdf`, { method: "POST" });
    setStatusMessage(`PDF regenerated for v${version}`);
    await refreshCaseDetail(selectedCaseId);
  };

  const onOpenPdf = (version: number): void => {
    if (!selectedCaseId) return;
    window.open(`/api/cases/${selectedCaseId}/proposals/${version}/pdf/file`, "_blank", "noopener,noreferrer");
  };

  const onDownloadPdf = (version: number): void => {
    if (!selectedCaseId) return;
    window.open(`/api/cases/${selectedCaseId}/proposals/${version}/pdf/file?download=1`, "_blank", "noopener,noreferrer");
  };

  const onDummySend = async (): Promise<void> => {
    if (!selectedCaseId) return;
    await fetch(`/api/cases/${selectedCaseId}/send`, { method: "POST" });
    setStatusMessage("Test submit recorded");
    await refreshCaseDetail(selectedCaseId);
  };

  const onSavePlaybook = async (): Promise<void> => {
    await fetch("/api/playbook", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: playbookContent })
    });
    setStatusMessage("Playbook saved");
  };

  const onSaveQuestionPrompt = async (): Promise<void> => savePromptTemplate("question-prompt", questionPromptContent);

  const onSavePlanPrompt = async (): Promise<void> => savePromptTemplate("plan-prompt", planPromptContent);

  const onSaveFeasibilityPrompt = async (): Promise<void> =>
    savePromptTemplate("feasibility-prompt", feasibilityPromptContent);

  const onSaveMarkdownFile = async (scope: CompanyScope): Promise<void> => {
    const path = scope === "profile" ? selectedProfilePath : selectedCvPath;
    const entry = scope === "profile" ? selectedProfileEntry : selectedCvEntry;
    const content = scope === "profile" ? profileEditorValue : cvEditorValue;
    if (!path || !entry?.isMarkdown) return;
    await fetch("/api/company/file", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope, path, content })
    });
    setStatusMessage("Markdown file saved");
    await refreshBrowser(scope);
  };

  const onCreateMarkdownFile = async (scope: CompanyScope): Promise<void> => {
    const directory = scope === "profile" ? newProfileDirectory : newCvDirectory;
    const fileName = scope === "profile" ? newProfileFileName : newCvFileName;
    if (!fileName.endsWith(".md")) {
      setStatusMessage("New file name must end with .md");
      return;
    }
    const response = await fetch("/api/company/create-md", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope, directory, fileName, content: "" })
    });
    if (!response.ok) {
      setStatusMessage("Could not create markdown file");
      return;
    }
    setStatusMessage("New markdown file created");
    await refreshBrowser(scope);
  };

  const onDeleteFile = async (scope: CompanyScope, path: string): Promise<boolean> => {
    const response = await fetch("/api/company/delete-file", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope, path })
    });
    if (!response.ok) {
      setStatusMessage("Failed to delete file");
      return false;
    }
    setStatusMessage("File deleted");
    if (scope === "profile" && selectedProfilePath === path) {
      setSelectedProfilePath("");
      setProfileEditorValue("");
    }
    if (scope === "cvs" && selectedCvPath === path) {
      setSelectedCvPath("");
      setCvEditorValue("");
    }
    await refreshBrowser(scope);
    return true;
  };

  const onImportLibraryFiles = async (scope: CompanyScope, event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const upload = event.target.files?.[0];
    if (!upload) return;
    event.target.value = "";
    const directory = scope === "profile" ? uploadProfileDirectory : uploadCvDirectory;
    const setImportActivity = scope === "profile" ? setProfileImportActivity : setCvImportActivity;
    setImportActivity({
      status: "running",
      title: `Importing ${upload.name}`,
      detail: directory ? `Uploading to ${directory}` : "Uploading and converting documents",
      progress: 12,
      error: null
    });
    const formData = new FormData();
    formData.set("scope", scope);
    formData.set("directory", directory);
    formData.set("file", upload);
    const response = await fetch("/api/company/import", { method: "POST", body: formData });
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({ error: "Document import failed" }))) as { error?: string };
      const errorMessage = payload.error?.trim() || "Document import failed";
      setImportActivity({
        status: "failed",
        title: `Import failed for ${upload.name}`,
        detail: "The upload could not be converted into library markdown.",
        progress: 100,
        error: errorMessage
      });
      setStatusMessage("Document import failed");
      return;
    }
    const payload = (await response.json()) as { jobId?: string };
    const jobId = payload.jobId?.trim();
    if (!jobId) {
      setImportActivity({
        status: "failed",
        title: `Import failed for ${upload.name}`,
        detail: "The upload could not be converted into library markdown.",
        progress: 100,
        error: "Import job was not created."
      });
      setStatusMessage("Document import failed");
      return;
    }

    while (true) {
      const statusResponse = await fetch(`/api/company/import-status?jobId=${encodeURIComponent(jobId)}`);
      if (!statusResponse.ok) {
        const statusPayload = (await statusResponse.json().catch(() => ({ error: "Document import failed" }))) as { error?: string };
        const errorMessage = statusPayload.error?.trim() || "Document import failed";
        setImportActivity({
          status: "failed",
          title: `Import failed for ${upload.name}`,
          detail: "The upload could not be converted into library markdown.",
          progress: 100,
          error: errorMessage
        });
        setStatusMessage("Document import failed");
        return;
      }

      const job = (await statusResponse.json()) as LibraryImportJobPayload;
      const nextStatus =
        job.status === "succeeded"
          ? "succeeded"
          : job.status === "failed"
            ? "failed"
            : "running";
      setImportActivity({
        status: nextStatus,
        title:
          nextStatus === "succeeded"
            ? `Imported ${upload.name}`
            : nextStatus === "failed"
              ? `Import failed for ${upload.name}`
              : `Importing ${upload.name}`,
        detail: job.detail?.trim() || "Converting documents to markdown",
        progress: Math.max(5, Math.min(100, Math.round(job.progress))),
        error: job.error
      });

      if (job.status === "succeeded") {
        const importedCount = job.importedPaths.length;
        setStatusMessage(
          importedCount > 0
            ? `Imported ${importedCount} markdown file${importedCount === 1 ? "" : "s"}`
            : "Documents imported"
        );
        await refreshBrowser(scope);
        return;
      }

      if (job.status === "failed") {
        setStatusMessage("Document import failed");
        return;
      }

      await wait(1000);
    }
  };

  const onSaveSettings = async (): Promise<void> => {
    await fetch("/api/settings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ defaultModel: settingsModel })
    });
    setStatusMessage("Settings saved");
  };

  const currentThemeLabel = useMemo(() => {
    if (theme === "system") return isDarkBySystemPreference() ? "Dark (System)" : "Light (System)";
    return theme === "dark" ? "Dark" : "Light";
  }, [theme]);
  const renderMainContent = (): JSX.Element => {
    if (activeItem === "tenders") {
      return <TendersPage />;
    }

    if (activeItem === "cases") {
      if (!selectedCaseId) {
        return (
          <CasesListPage
            cases={cases}
            selectedCaseIds={selectedCaseIds}
            statusMessage={statusMessage}
            importTenderInputRef={importTenderInputRef}
            onImportZip={onImportZip}
            onOpenCase={(caseId) => {
              setActiveItem("cases");
              setSelectedCaseId(caseId);
              setSelectedCasePage("overview");
              setSelectedImportFilePath("");
            }}
            onToggleCaseSelection={onToggleCaseSelection}
            onToggleSelectVisibleCases={onToggleSelectVisibleCases}
            isDeleteCasesDialogOpen={isDeleteCasesDialogOpen}
            onSetDeleteCasesDialogOpen={setIsDeleteCasesDialogOpen}
            isDeletingCases={isDeletingCases}
            onDeleteSelectedCases={onDeleteSelectedCases}
          />
        );
      }

      return (
        <CaseWorkspacePage
          selectedCase={selectedCase}
          selectedCaseId={selectedCaseId}
          selectedCasePage={selectedCasePage}
          onSelectPage={setSelectedCasePage}
          onBackToCases={() => {
            setSelectedCaseId(null);
            setSelectedCasePage("overview");
            setSelectedImportFilePath("");
          }}
          statusMessage={statusMessage}
          caseJobs={caseJobs}
          caseLogs={caseLogs}
          activityEvents={activityEvents}
          caseQuestions={caseQuestions}
          casePlan={casePlan}
          caseProposals={caseProposals}
          caseImportFiles={caseImportFiles}
          caseFeasibility={caseFeasibility}
          caseReadiness={caseReadiness}
          selectedImportLogJobId={selectedImportLogJobId}
          selectedImportLogFiles={selectedImportLogFiles}
          onToggleImportLogDetails={onToggleImportLogDetails}
          notesDraft={notesDraft}
          onChangeNotesDraft={setNotesDraft}
          onSaveCaseMeta={onSaveCaseMeta}
          autoSaveNotes={autoSaveNotes}
          onGenerateQuestions={onGenerateQuestions}
          onGeneratePlan={onGeneratePlan}
          onRunProposal={onRunProposal}
          questionAnswerDrafts={questionAnswerDrafts}
          onUpdateQuestionAnswer={onUpdateQuestionAnswer}
          onSaveAnswer={onSaveAnswer}
          autoSaveAnswer={autoSaveAnswer}
          planMarkdown={planMarkdown}
          onChangePlanMarkdown={setPlanMarkdown}
          onSavePlan={onSavePlan}
          proposalNotes={proposalNotes}
          onChangeProposalNotes={setProposalNotes}
          proposalFeedback={proposalFeedback}
          onChangeProposalFeedback={setProposalFeedback}
          proposalChatDraft={proposalChatDraft}
          onChangeProposalChatDraft={setProposalChatDraft}
          onRegeneratePdf={onRegeneratePdf}
          onOpenPdf={onOpenPdf}
          onDownloadPdf={onDownloadPdf}
          onDummySend={onDummySend}
          onSendProposalChatMessage={onSendProposalChatMessage}
          selectedImportFilePath={selectedImportFilePath}
          onSelectImportFile={setSelectedImportFilePath}
          importViewerMarkdownContent={importViewerMarkdownContent}
          importViewerError={importViewerError}
          isImportViewerLoading={isImportViewerLoading}
        />
      );
    }

    if (activeItem === "playbook") {
      return (
        <PlaybookPage
          value={playbookContent}
          onChange={setPlaybookContent}
          onSave={onSavePlaybook}
          statusMessage={statusMessage}
        />
      );
    }

    if (activeItem === "question-prompt") {
      return (
        <PromptTemplatePage
          fileName="question-prompt.md"
          description="Template the AI uses to generate strategic steering questions from tender documents."
          value={questionPromptContent}
          onChange={setQuestionPromptContent}
          onSave={onSaveQuestionPrompt}
          statusMessage={statusMessage}
          placeholder="Write the reusable steering question generation prompt template."
        />
      );
    }

    if (activeItem === "plan-prompt") {
      return (
        <PromptTemplatePage
          fileName="plan-prompt.md"
          description="Template for structuring the proposal outline from answered questions."
          value={planPromptContent}
          onChange={setPlanPromptContent}
          onSave={onSavePlanPrompt}
          statusMessage={statusMessage}
          placeholder="Write the reusable outline generation prompt template."
        />
      );
    }

    if (activeItem === "feasibility-prompt") {
      return (
        <PromptTemplatePage
          fileName="feasibility-prompt.md"
          description="Template for the initial go/no-go bid assessment run at import."
          value={feasibilityPromptContent}
          onChange={setFeasibilityPromptContent}
          onSave={onSaveFeasibilityPrompt}
          statusMessage={statusMessage}
          placeholder="Write the reusable feasibility-evaluation prompt template."
        />
      );
    }

    if (activeItem === "profile-references") {
      return (
        <DocumentLibraryPage
          scope="profile"
          description="Company profile, past projects, and reusable content included in every proposal."
          importActivity={profileImportActivity}
          entries={profileEntries}
          selectedPath={selectedProfilePath}
          onSelectPath={setSelectedProfilePath}
          selectedEntry={selectedProfileEntry}
          editorValue={profileEditorValue}
          onChangeEditor={setProfileEditorValue}
          onSaveMarkdown={() => onSaveMarkdownFile("profile")}
          createDirectory={newProfileDirectory}
          onChangeCreateDirectory={setNewProfileDirectory}
          createFileName={newProfileFileName}
          onChangeCreateFileName={setNewProfileFileName}
          onCreateMarkdownFile={() => onCreateMarkdownFile("profile")}
          uploadDirectory={uploadProfileDirectory}
          onChangeUploadDirectory={setUploadProfileDirectory}
          onImportFiles={(event) => onImportLibraryFiles("profile", event)}
          onDeleteFile={(path) => onDeleteFile("profile", path)}
          statusMessage={statusMessage}
        />
      );
    }

    if (activeItem === "cvs") {
      return (
        <DocumentLibraryPage
          scope="cvs"
          description="Team expertise and experience included as context when generating proposals."
          importActivity={cvImportActivity}
          entries={cvEntries}
          selectedPath={selectedCvPath}
          onSelectPath={setSelectedCvPath}
          selectedEntry={selectedCvEntry}
          editorValue={cvEditorValue}
          onChangeEditor={setCvEditorValue}
          onSaveMarkdown={() => onSaveMarkdownFile("cvs")}
          createDirectory={newCvDirectory}
          onChangeCreateDirectory={setNewCvDirectory}
          createFileName={newCvFileName}
          onChangeCreateFileName={setNewCvFileName}
          onCreateMarkdownFile={() => onCreateMarkdownFile("cvs")}
          uploadDirectory={uploadCvDirectory}
          onChangeUploadDirectory={setUploadCvDirectory}
          onImportFiles={(event) => onImportLibraryFiles("cvs", event)}
          onDeleteFile={(path) => onDeleteFile("cvs", path)}
          statusMessage={statusMessage}
        />
      );
    }

    return (
      <SettingsPage
        availableModels={CLAUDE_MODELS}
        selectedModel={settingsModel}
        onSelectModel={setSettingsModel}
        onSave={onSaveSettings}
        statusMessage={statusMessage}
      />
    );
  };

  const locksShellScroll =
    activeItem === "cases" &&
    selectedCaseId !== null &&
    selectedCasePage === "activity";

  return (
    <SidebarProvider>
      <AppSidebar
        navGroups={navGroups}
        activeItem={activeItem}
        onSelectItem={(itemId) => {
          const nextRoute = routeForNavSelection(itemId);
          setActiveItem(nextRoute.activeItem);
          setSelectedCaseId(nextRoute.selectedCaseId);
          setSelectedCasePage(nextRoute.casePage);
          setSelectedImportFilePath(nextRoute.selectedImportFilePath ?? "");
        }}
        themeLabel={currentThemeLabel}
        onToggleTheme={() => void toggleTheme()}
        userLabel="ole@framna.com"
        onSignOut={() => {
          void authClient.signOut();
        }}
        ThemeIcon={ThemeIcon}
      />
      <SidebarInset className="framna-shell h-svh overflow-hidden p-4 md:p-6">
        <header className="mb-6 shrink-0 flex items-center gap-3">
          <SidebarTrigger className="rounded-full border border-black/10 bg-white/70 shadow-sm backdrop-blur-sm hover:bg-white" />
          <h1 className="font-editorial text-4xl leading-none text-balance sm:text-5xl">{getNavLabel(activeItem)}</h1>
        </header>
        <div
          className={cn(
            "min-h-0 flex-1",
            locksShellScroll ? "overflow-hidden" : "overflow-y-auto overscroll-contain"
          )}
        >
          {renderMainContent()}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export const App = (): JSX.Element => {
  const { data: session, isPending } = authClient.useSession();

  useEffect(() => {
    if (!session || typeof window === "undefined" || window.location.pathname !== "/login") {
      return;
    }

    window.history.replaceState({}, "", "/cases");
  }, [session]);

  if (isPending) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="space-y-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">FramnaBid</p>
          <h1 className="font-editorial text-4xl">Loading workspace</h1>
        </div>
      </main>
    );
  }

  if (!session) {
    return <LoginScreen />;
  }

  return <AuthenticatedApp />;
};
